import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Membership360.css';
import {
  GROWTH,
  MONTH_GET,
  PILLARS,
  SCARCITY,
  whatsappHref,
} from './orbitContent';
import OrbitFaq from './OrbitFaq';

function polarPosition(index, total, radiusPercent) {
  const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
  const x = 50 + radiusPercent * Math.cos(angle);
  const y = 50 + radiusPercent * Math.sin(angle);
  return { left: `${x}%`, top: `${y}%` };
}

function CtaWhatsApp({ className, children }) {
  return (
    <a
      className={className}
      href={whatsappHref()}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

function HeroOrbit() {
  const visualRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const fine = window.matchMedia('(pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduce) return undefined;

    const el = visualRef.current;
    if (!el) return undefined;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      setTilt({ x: py * -8, y: px * 10 });
    };
    const onLeave = () => setTilt({ x: 0, y: 0 });

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return (
    <section className="m360-hero">
      <div className="m360-wrap m360-hero-grid">
        <div className="m360-hero-copy">
          <p className="m360-brand">Unschooling</p>
          <p className="m360-product-tag">Orbit · Hyderabad pilot</p>
          <h1>One month. One full curiosity rhythm.</h1>
          <p className="m360-hero-sub">
            Growing by doing — not more teaching. One honest month.
          </p>
          <div className="m360-cta-row">
            <Link className="m360-btn m360-btn-primary" to="/orbit/join#interest">
              {SCARCITY.cta}
            </Link>
            <Link className="m360-btn m360-btn-ghost" to="/orbit/curiosity-kit">
              Open Curiosity Kit
            </Link>
          </div>
        </div>
        <div className="m360-hero-visual" aria-hidden="true" ref={visualRef}>
          <div
            className="m360-hero-tilt"
            style={{
              transform: `perspective(700px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            }}
          >
            <div className="m360-orbit" />
            <div className="m360-hero-ring">
              <div className="m360-hero-core">
                <strong>Orbit</strong>
                <span>Monthly curiosity rhythm</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowKidsGrow() {
  return (
    <section className="m360-section" id="grow" data-reveal>
      <div className="m360-wrap">
        <span className="m360-kicker">How kids grow</span>
        <h2>Four muscles parents can see</h2>
        <p className="m360-lead">
          What parents often notice — not careers or exam scores. Tap one.
        </p>
        <div className="m360-growth-grid">
          {GROWTH.map((g) => (
            <Link key={g.muscle} to={g.to} className="m360-growth-card m360-card-link">
              <h3>{g.muscle}</h3>
              <p className="m360-growth-how">{g.how}</p>
              <p className="m360-growth-notice">“{g.notice}”</p>
              <span className="m360-card-cta">See what you get →</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatYouGet() {
  return (
    <section className="m360-section" id="month-get" data-reveal>
      <div className="m360-wrap">
        <span className="m360-kicker">What you get this month</span>
        <h2>Starting rhythm — then shape around your child</h2>
        <p className="m360-lead">
          Kit → missions → meetup → brave → achievements wall. The core we
          promise — interests can stretch it. Tap any piece, or open the full map.
        </p>
        <div className="m360-month-get">
          {MONTH_GET.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="m360-month-get-item m360-card-link"
            >
              <div className="m360-item-head">
                <span className="ico od-ico" aria-hidden="true">
                  {item.icon}
                </span>
                <h3>{item.title}</h3>
              </div>
              <p>{item.text}</p>
              <span className="m360-card-cta">Open page →</span>
            </Link>
          ))}
        </div>
        <p className="m360-mission-note">
          <Link to="/orbit/what-you-get">Full map of everything you get</Link>
        </p>
      </div>
    </section>
  );
}

function PillarWheel() {
  const [active, setActive] = useState(0);
  const pillar = PILLARS[active];

  return (
    <section className="m360-section m360-section-alt" id="pillars" data-reveal>
      <div className="m360-wrap">
        <span className="m360-kicker">Inside each Orbit month</span>
        <h2>Ten ways the month can show up</h2>
        <p className="m360-lead">
          Tap a piece for how the month can show up — themes stay open, not a
          fixed subject list.
        </p>
        <div className="m360-wheel-layout">
          <div className="m360-wheel" role="list">
            <div className="m360-wheel-center">
              <strong>Orbit</strong>
              <span>Tap a spark</span>
            </div>
            {PILLARS.map((p, i) => {
              const pos = polarPosition(i, PILLARS.length, 38);
              return (
                <button
                  key={p.id}
                  type="button"
                  role="listitem"
                  className={`m360-pillar-dot${active === i ? ' is-active' : ''}`}
                  style={{ ...pos, background: p.color, '--dot-i': i }}
                  aria-label={p.title}
                  aria-pressed={active === i}
                  onClick={() => setActive(i)}
                >
                  {p.icon}
                </button>
              );
            })}
          </div>
          <div>
            <div className="m360-pillar-detail is-swap" key={pillar.id}>
              <span className="tag" style={{ background: pillar.tagBg }}>
                Piece {active + 1} of 10
              </span>
              <h3 className="m360-pillar-title">
                <span className="od-ico" aria-hidden="true">
                  {pillar.icon}
                </span>
                <span>{pillar.title}</span>
              </h3>
              <p>{pillar.blurb}</p>
              <p className="m360-var-label">Example variations</p>
              <ul className="m360-var-list">
                {pillar.variations.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
              <div className="m360-cta-row m360-pillar-ctas">
                <Link className="m360-btn m360-btn-primary" to={pillar.to}>
                  What you get
                </Link>
                <Link
                  className="m360-btn m360-btn-ghost"
                  to={`/orbit/ways/${pillar.id}`}
                >
                  All variations
                </Link>
              </div>
            </div>
            <div className="m360-pillar-list">
              {PILLARS.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  className={`m360-pillar-chip${active === i ? ' is-active' : ''}`}
                  onClick={() => setActive(i)}
                >
                  {p.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PilotOffer() {
  return (
    <section className="m360-section" id="pilot" data-reveal>
      <div className="m360-wrap">
        <span className="m360-kicker">How to join</span>
        <h2>First Orbit chapter</h2>
        <p className="m360-lead">
          Ages 5–8 · Hyderabad · ₹0 · we fund the kit + meetup. A mission month,
          not an admission — open to 5 families, then this chapter closes.
        </p>
        <div className="m360-cta-row" style={{ marginBottom: '0.5rem' }}>
          <Link className="m360-btn m360-btn-primary" to="/orbit/join#interest">
            {SCARCITY.cta}
          </Link>
          <CtaWhatsApp className="m360-btn m360-btn-ghost">
            WhatsApp us
          </CtaWhatsApp>
        </div>
      </div>
    </section>
  );
}

function ParentFAQ() {
  return (
    <section className="m360-section m360-section-alt" id="faq" data-reveal>
      <div className="m360-wrap">
        <span className="m360-kicker">Parents ask</span>
        <h2>Quick joining answers</h2>
        <p className="m360-lead">
          Only what’s unique to joining — more on the FAQ page.
        </p>
        <OrbitFaq limit={4} groupDefault="Joining" showFilters={false} />
        <p className="m360-mission-note">
          More questions? <Link to="/orbit/faq">Open the full FAQ</Link>
          {' · '}
          <a href={whatsappHref()} target="_blank" rel="noopener noreferrer">
            WhatsApp us
          </a>
        </p>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="m360-final" data-reveal>
      <div className="m360-wrap">
        <div className="m360-final-banner">
          <h2>Ready for one honest Orbit month?</h2>
          <div className="m360-cta-row">
            <Link className="m360-btn m360-btn-primary" to="/orbit/join#interest">
              {SCARCITY.cta}
            </Link>
            <Link className="m360-btn m360-btn-ghost" to="/orbit/curiosity-kit">
              Curiosity Kit details
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const Membership360Page = () => {
  return (
    <main className="m360">
      <HeroOrbit />
      <WhatYouGet />
      <HowKidsGrow />
      <PillarWheel />
      <PilotOffer />
      <ParentFAQ />
      <FinalCTA />
    </main>
  );
};

export default Membership360Page;
